import type { FC } from 'react';
import { useState } from 'react';
import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';

const PrivacyPolicyPage: FC = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    { id: 'introduction', title: '1. Introduction' },
    { id: 'data-controller', title: '2. Data Controller Information' },
    { id: 'information-collect', title: '3. Information We Collect' },
    { id: 'how-we-use', title: '4. How We Use Your Information' },
    { id: 'ai-features', title: '5. AI Features and Data Processing' },
    { id: 'how-we-share', title: '6. How We Share Your Information' },
    { id: 'international-transfers', title: '7. International Data Transfers' },
    { id: 'data-retention', title: '8. Data Retention' },
    { id: 'cookies', title: '9. Cookies and Tracking' },
    { id: 'your-rights', title: '10. Your Privacy Rights' },
    { id: 'data-security', title: '11. Data Security' },
    { id: 'california', title: '12. California-Specific Notices' },
    { id: 'changes', title: '13. Changes to This Policy' },
    { id: 'contact', title: '14. Contact Us' },
  ];

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NextSeo
        title="Privacy Policy"
        description="Learn how Chat.co collects, uses, and protects your personal information when you use our AI-powered chatbot platform."
      />
      <Header />

      {/* Hero Section */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-md bg-primary-100 px-3 py-1.5">
              <Icon icon="tabler:shield-check" className="text-base text-primary-700" />
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-700">
                Legal
              </span>
            </div>
            <h1 className="mb-4 text-5xl font-bold tracking-tight text-gray-900">Privacy Policy</h1>
            <p className="text-lg text-gray-600">
              Effective Date: January 7, 2026 | Last Updated: September 25, 2026
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex gap-12 py-12">
          {/* Sidebar Navigation */}
          <aside className="hidden w-64 flex-shrink-0 lg:block">
            <div className="sticky top-24">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="max-w-3xl flex-1">
            <div className="prose prose-slate max-w-none">
              {/* Section 1: Introduction */}
              <section id="introduction" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">1. Introduction</h2>
                <div className="space-y-4 text-base leading-relaxed text-gray-700">
                  <p>
                    SA Cyber LLC, a Wyoming limited liability company doing business as "Chat.co"
                    ("Chat.co," "we," "us," or "our"), respects your privacy and is committed to
                    protecting your personal data. This Privacy Policy explains how we collect, use,
                    disclose, and safeguard your information when you visit our website, use our
                    AI-powered chatbot platform, or interact with our Services.
                  </p>
                  <p>
                    This Privacy Policy applies to information we collect through our website at{' '}
                    <a
                      href="https://chat.co"
                      className="font-medium text-primary-600 hover:text-primary-700">
                      chat.co
                    </a>
                    , our platform and dashboard, our APIs, embeddable chat widgets, and any other
                    services we provide (collectively, the "Services").
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  1.1 Scope of This Policy
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  This Privacy Policy applies when Chat.co acts as a data controller—that is, when
                  we determine the purposes and means of processing your personal data. This
                  includes when you:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Visit our website or marketing pages</li>
                  <li>Create an account and use our platform</li>
                  <li>Subscribe to our newsletter or marketing communications</li>
                  <li>Contact our support team</li>
                  <li>Apply for employment with us</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  1.2 When This Policy Does Not Apply
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  When our customers use Chat.co to create and deploy chatbots, we act as a data
                  processor on their behalf. In these cases, our customers are the data controllers
                  and their privacy policies govern how end-user data is collected and used. If you
                  interact with a chatbot deployed by one of our customers, please refer to that
                  organization's privacy policy for information about their data practices.
                </p>
                <p className="text-base leading-relaxed text-gray-700">
                  For questions about data processed through a customer's chatbot, please contact
                  that organization directly.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  1.3 Important Information
                </h3>
                <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5">
                  <p className="text-sm font-medium text-amber-900">
                    Please read this Privacy Policy carefully. By accessing or using our Services,
                    you acknowledge that you have read, understood, and agree to be bound by this
                    Privacy Policy. If you do not agree with our policies and practices, please do
                    not use our Services.
                  </p>
                </div>
              </section>

              {/* Section 2: Data Controller Information */}
              <section id="data-controller" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  2. Data Controller Information
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  For the purposes of applicable data protection laws, the data controller is:
                </p>
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6">
                  <p className="text-base font-semibold text-gray-900">
                    SA Cyber LLC d/b/a Chat.co
                  </p>
                  <p className="mt-2 text-base text-gray-700">
                    Email:{' '}
                    <a
                      href="mailto:privacy@chat.co"
                      className="text-primary-600 hover:text-primary-700">
                      privacy@chat.co
                    </a>
                  </p>
                  <p className="text-base text-gray-700">
                    Website:{' '}
                    <a href="https://chat.co" className="text-primary-600 hover:text-primary-700">
                      https://chat.co
                    </a>
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  2.1 Data Protection Officer
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  We have appointed a Data Protection Officer (DPO) who is responsible for
                  overseeing questions in relation to this Privacy Policy. If you have any questions
                  about this Privacy Policy, including any requests to exercise your legal rights,
                  please contact our DPO at:
                </p>
                <p className="text-base text-gray-700">
                  Email:{' '}
                  <a href="mailto:dpo@chat.co" className="text-primary-600 hover:text-primary-700">
                    dpo@chat.co
                  </a>
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">2.2 EU Representative</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If you are located in the European Economic Area (EEA) or the United Kingdom (UK),
                  you may contact our EU representative for data protection matters. Contact details
                  are available upon request at{' '}
                  <a
                    href="mailto:privacy@chat.co"
                    className="text-primary-600 hover:text-primary-700">
                    privacy@chat.co
                  </a>
                  .
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  2.3 Brazil Representative (Encarregado)
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  For users located in Brazil, our Data Protection Officer also serves as our
                  Encarregado under the Lei Geral de Proteção de Dados (LGPD). Contact:{' '}
                  <a href="mailto:dpo@chat.co" className="text-primary-600 hover:text-primary-700">
                    dpo@chat.co
                  </a>
                  .
                </p>
              </section>

              {/* Section 3: Information We Collect */}
              <section id="information-collect" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">3. Information We Collect</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  We collect information in several ways: directly from you, automatically when you
                  use our Services, and from third parties. The types of information we collect
                  depend on how you interact with us.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  3.1 Information You Provide Directly
                </h3>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  3.1.1 Account Information
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When you create an account, we collect:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Name and email address</li>
                  <li>Password (stored in encrypted form)</li>
                  <li>Company or organization name</li>
                  <li>Job title and role</li>
                  <li>Phone number (optional)</li>
                  <li>Profile picture (optional)</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  3.1.2 Payment Information
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When you make a purchase, our payment processor (Stripe) collects:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Credit or debit card number</li>
                  <li>Billing address</li>
                  <li>Transaction history</li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  We do not store your full payment card details. Payment processing is handled
                  entirely by Stripe, Inc. in accordance with PCI-DSS standards. Please review
                  Stripe's Privacy Policy for information about their data practices.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  3.1.3 Customer Content
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When you use our platform, you may upload or create:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Documents for Knowledge Bases (PDFs, Word documents, Excel files, text files)
                  </li>
                  <li>Chatbot configurations and customizations</li>
                  <li>System prompts and training data</li>
                  <li>Question and answer pairs</li>
                  <li>Custom branding and appearance settings</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  3.1.4 Communications
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When you contact us, we collect:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Support request content and attachments</li>
                  <li>Feedback and survey responses</li>
                  <li>Email correspondence</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  3.2 Information Collected Automatically
                </h3>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">3.2.1 Usage Data</h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We automatically collect information about how you use our Services:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Pages visited and features used</li>
                  <li>Time spent on pages</li>
                  <li>Click patterns and navigation paths</li>
                  <li>Search queries within the platform</li>
                  <li>Chatbot creation and configuration activities</li>
                  <li>Error logs and performance data</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  3.2.2 Device and Technical Information
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We collect information about your device and connection:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>IP address (used to derive approximate location at city/region level)</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Device type (desktop, mobile, tablet)</li>
                  <li>Screen resolution</li>
                  <li>Language preferences</li>
                  <li>Referring URL</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  3.2.3 Cookies and Similar Technologies
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  We use cookies, pixels, and similar tracking technologies to collect information.
                  See Section 9 (Cookies and Tracking Technologies) for detailed information about
                  our cookie practices and your choices.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  3.3 Information from Third Parties
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We may receive information about you from:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Single sign-on providers (Google, Microsoft) if you choose to authenticate
                    through them
                  </li>
                  <li>Analytics providers (aggregated usage data)</li>
                  <li>Marketing partners (business contact information for B2B outreach)</li>
                  <li>Publicly available sources (company information, professional profiles)</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  3.4 Sensitive Personal Data
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We do not intentionally collect sensitive personal data, including:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Racial or ethnic origin</li>
                  <li>Political opinions or religious beliefs</li>
                  <li>Genetic or biometric data</li>
                  <li>Health information</li>
                  <li>Sexual orientation</li>
                  <li>Precise geolocation data</li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  If you upload documents containing sensitive data to your Knowledge Base, you are
                  responsible for ensuring you have the appropriate legal basis and consent to
                  process such data.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  3.5 Children's Privacy
                </h3>
                <div className="my-6 rounded-lg border-l-4 border-red-500 bg-red-50 p-5">
                  <p className="text-sm font-medium text-red-900">
                    <strong>
                      Our Services are not intended for individuals under 18 years of age.
                    </strong>{' '}
                    We do not knowingly collect personal information from anyone under 18. If you
                    are under 18, please do not use our Services or provide any personal
                    information. If we learn that we have collected personal information from a
                    child under 18 without verification of parental consent, we will delete that
                    information promptly. If you believe we have collected information from a child
                    under 18, please contact us at{' '}
                    <a href="mailto:privacy@chat.co" className="underline">
                      privacy@chat.co
                    </a>
                    .
                  </p>
                </div>
                <h3
                  id="external-connections"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  3.6 Connected External Applications
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When you choose to connect an external application to Chat.co, we record the
                  connected application, your account and selected workspace, the permissions you
                  consent to, and connection and revocation information. We process the questions,
                  searches, expert and conversation identifiers, and source requests that the
                  application sends to carry out your requests.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The application receives the authorized expert information, answers, source
                  excerpts, citations, supporting-document links, and operation or credit-usage
                  information needed for those requests. If requested and authorized during sign-in,
                  it may also receive your account identifier, email address, and email-verification
                  status. Available experts and documents depend on your account and its current
                  permissions.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The connector does not request the external application’s full conversation
                  history, memory, or unrelated files. Content you include in a question is
                  processed as part of that question. Requests may be processed by the model
                  provider configured for your expert and, where needed for your request, the
                  expert’s reviewed functions.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The external application handles information it receives under its own terms and
                  privacy policy. Review those policies before connecting. You can revoke a
                  connection on the{' '}
                  <Link href="/connect" className="text-primary-600 underline">
                    Connected applications
                  </Link>{' '}
                  page. Revocation stops future connector access; it cannot retract answers or
                  documents already received by that application or delete its copies.
                  Supporting-document access through Chat.co remains subject to current
                  authorization and source availability.
                </p>
              </section>

              {/* Section 4: How We Use Your Information */}
              <section id="how-we-use" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  4. How We Use Your Information
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  We use the information we collect for the purposes described below. For users in
                  the European Economic Area (EEA), United Kingdom (UK), or Brazil, we have
                  identified the legal basis for each processing activity.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.1 To Provide and Maintain Our Services
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Create and manage your account</li>
                  <li>Process your transactions and send related information</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Enable you to create, configure, and deploy chatbots</li>
                  <li>Process and index documents for Knowledge Bases</li>
                  <li>Generate AI-powered chatbot responses</li>
                </ul>
                <p className="mt-3 text-sm italic text-gray-500">
                  Legal Basis (GDPR/LGPD): Performance of a contract with you.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.2 To Improve and Develop Our Services
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Analyze usage patterns to improve features and user experience</li>
                  <li>Conduct research and development</li>
                  <li>Test new features and functionality</li>
                  <li>Generate aggregated, anonymized analytics and benchmarks</li>
                </ul>
                <p className="mt-3 text-sm italic text-gray-500">
                  Legal Basis (GDPR/LGPD): Legitimate interests in improving our Services.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.3 To Communicate With You
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Send service-related notices (updates, security alerts, support messages)</li>
                  <li>Send marketing communications (with your consent where required)</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Provide information about new features, products, or services</li>
                </ul>
                <p className="mt-3 text-sm italic text-gray-500">
                  Legal Basis (GDPR/LGPD): Contract performance (service notices); Consent
                  (marketing); Legitimate interests (customer relationship management).
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.4 To Ensure Security and Prevent Fraud
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Monitor for suspicious activity and potential security threats</li>
                  <li>Prevent fraud, abuse, and violations of our Terms of Service</li>
                  <li>Protect the rights, property, and safety of Chat.co and our users</li>
                  <li>Investigate and respond to security incidents</li>
                </ul>
                <p className="mt-3 text-sm italic text-gray-500">
                  Legal Basis (GDPR/LGPD): Legitimate interests in security and fraud prevention;
                  Legal obligations.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.5 To Comply With Legal Obligations
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Comply with applicable laws, regulations, and legal processes</li>
                  <li>Respond to lawful requests from public authorities</li>
                  <li>Establish, exercise, or defend legal claims</li>
                  <li>Maintain records as required by law (tax, accounting)</li>
                </ul>
                <p className="mt-3 text-sm italic text-gray-500">
                  Legal Basis (GDPR/LGPD): Legal obligation; Legitimate interests in legal
                  compliance.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">4.6 With Your Consent</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  We may process your information for other purposes with your explicit consent. You
                  may withdraw consent at any time, but this will not affect the lawfulness of
                  processing before withdrawal.
                </p>
              </section>

              {/* Section 5: AI Features and Data Processing */}
              <section id="ai-features" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  5. AI Features and Data Processing
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  Our Services utilize artificial intelligence and machine learning technologies to
                  provide chatbot functionality. This section explains how your data is processed in
                  connection with AI features.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.1 How AI Features Work
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When you use Chat.co to create chatbots:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Documents you upload are processed to extract text and create vector embeddings
                  </li>
                  <li>
                    These embeddings are stored in our secure databases to enable semantic search
                  </li>
                  <li>
                    When end-users interact with your chatbot, their queries are processed by AI
                    models to generate responses
                  </li>
                  <li>
                    Responses are generated based on your Knowledge Base content and chatbot
                    configuration
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.2 Third-Party AI Providers
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  We use third-party AI services to power certain features. These providers process
                  data to generate AI responses but operate under our commercial agreements with
                  specific data protection terms:
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">5.2.1 AWS Bedrock</h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Provider:</strong> Amazon Web Services, Inc.
                  </li>
                  <li>
                    <strong>Purpose:</strong> AI model hosting and inference
                  </li>
                  <li>
                    <strong>Data Retention:</strong> Zero data retention for inference by default
                  </li>
                  <li>
                    <strong>Training:</strong> Your data is NOT used to train AWS foundation models
                  </li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">5.2.2 OpenAI</h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Provider:</strong> OpenAI, Inc.
                  </li>
                  <li>
                    <strong>Purpose:</strong> Large language model processing
                  </li>
                  <li>
                    <strong>Training:</strong> Under our API/Business Terms, OpenAI does NOT use API
                    inputs or outputs to train their models
                  </li>
                  <li>
                    <strong>Data Retention:</strong> Subject to OpenAI's data retention policies for
                    API usage
                  </li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">5.2.3 Anthropic</h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Provider:</strong> Anthropic PBC
                  </li>
                  <li>
                    <strong>Purpose:</strong> Large language model processing
                  </li>
                  <li>
                    <strong>Training:</strong> Under our Commercial Terms, Anthropic does NOT use
                    API data to train their models
                  </li>
                  <li>
                    <strong>Data Retention:</strong> Subject to Anthropic's data retention policies
                    for API usage
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  5.2.4 Other Configured Model Providers
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The provider used for an expert depends on its configuration. Google Gemini and
                  Grok models may also process the questions and relevant context needed to generate
                  a response. Provider processing and retention depend on the service, account
                  settings, and applicable business or API terms; connecting an external application
                  does not create a separate zero-retention guarantee.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Provider privacy and data-processing information is available from{' '}
                  <a href="https://aws.amazon.com/privacy/" className="text-primary-600 underline">
                    AWS
                  </a>
                  ,{' '}
                  <a
                    href="https://openai.com/policies/privacy-policy/"
                    className="text-primary-600 underline">
                    OpenAI
                  </a>
                  ,{' '}
                  <a
                    href="https://www.anthropic.com/legal/privacy"
                    className="text-primary-600 underline">
                    Anthropic
                  </a>
                  ,{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    className="text-primary-600 underline">
                    Google
                  </a>{' '}
                  and its{' '}
                  <a
                    href="https://ai.google.dev/gemini-api/terms"
                    className="text-primary-600 underline">
                    Gemini API data terms
                  </a>
                  , and the{' '}
                  <a
                    href="https://x.ai/legal/data-processing-addendum"
                    className="text-primary-600 underline">
                    Grok API Data Processing Addendum
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://x.ai/legal/faq-enterprise"
                    className="text-primary-600 underline">
                    enterprise data-handling information
                  </a>
                  .
                </p>
                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.3 Our AI Training Practices
                </h3>
                <div className="my-6 rounded-lg border-l-4 border-green-500 bg-green-50 p-5">
                  <p className="text-sm font-medium text-green-900">
                    <strong>We do NOT use your Customer Content</strong> (documents, chat logs, or
                    AI outputs) to train our own AI models or foundation models generally available
                    to other customers.
                  </p>
                </div>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We may use the following data to improve our Services:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Aggregated, anonymized usage statistics</li>
                  <li>Technical performance metrics</li>
                  <li>Error logs and debugging information (with personal data removed)</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.4 AI Output Limitations
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  AI-generated content may contain inaccuracies, errors, or inappropriate content
                  ("hallucinations"). We recommend:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Human review of AI outputs before relying on them for important decisions</li>
                  <li>
                    Not using AI features for medical, legal, financial, or other professional
                    advice
                  </li>
                  <li>Verifying factual claims generated by AI</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.5 Opting Out of AI Processing
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If you do not want your data processed by AI features, you may:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Not upload documents to Knowledge Bases</li>
                  <li>Disable AI-powered features in your chatbot configuration</li>
                  <li>Delete your Knowledge Base data at any time</li>
                  <li>Contact us to discuss alternative configurations</li>
                </ul>
              </section>

              {/* Section 6: How We Share Your Information */}
              <section id="how-we-share" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  6. How We Share Your Information
                </h2>
                <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-5">
                  <p className="text-sm font-medium text-blue-900">
                    <strong>We do not sell your personal information.</strong> We may share your
                    information in the following circumstances:
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.1 Service Providers (Sub-Processors)
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  We share information with third-party vendors who perform services on our behalf.
                  These providers are contractually obligated to use your data only as directed by
                  us and to maintain appropriate security measures.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.2 Business Transfers
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If Chat.co is involved in a merger, acquisition, reorganization, bankruptcy, or
                  sale of assets, your information may be transferred as part of that transaction.
                  We will notify you via email and/or a prominent notice on our website of any
                  change in ownership or uses of your personal information.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.3 Legal Requirements
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We may disclose your information if required to do so by law or in response to
                  valid requests by public authorities (e.g., a court or government agency). We may
                  also disclose information when we believe in good faith that disclosure is
                  necessary to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Comply with a legal obligation</li>
                  <li>Protect and defend our rights or property</li>
                  <li>Prevent or investigate possible wrongdoing</li>
                  <li>Protect the personal safety of users or the public</li>
                  <li>Protect against legal liability</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">6.4 With Your Consent</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  We may share your information with third parties when you have given us explicit
                  consent to do so.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.5 Aggregated or De-Identified Data
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  We may share aggregated or de-identified information that cannot reasonably be
                  used to identify you. This data may be used for industry analysis, benchmarking,
                  and other business purposes.
                </p>
              </section>

              {/* Section 7: International Data Transfers */}
              <section id="international-transfers" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  7. International Data Transfers
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  Chat.co is based in the United States. If you access our Services from outside the
                  United States, your information will be transferred to, stored, and processed in
                  the United States and potentially other countries where our service providers
                  operate.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.1 Transfers from the European Economic Area (EEA) and UK
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  For transfers of personal data from the EEA and UK to the United States, we rely
                  on the following transfer mechanisms:
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  7.1.1 EU-U.S. Data Privacy Framework
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  We are committed to complying with the EU-U.S. Data Privacy Framework (DPF), the
                  UK Extension to the EU-U.S. DPF, and the Swiss-U.S. DPF as set forth by the U.S.
                  Department of Commerce. We are in the process of self-certifying our compliance
                  through the Data Privacy Framework program.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  7.1.2 Standard Contractual Clauses
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Where required, we use the European Commission's Standard Contractual Clauses
                  (SCCs) approved in June 2021 (Module 2: Controller to Processor) to provide
                  adequate safeguards for international data transfers. These clauses are
                  incorporated into our Data Processing Agreement.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.2 Transfers from Brazil
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  For transfers of personal data from Brazil to the United States, we implement the
                  Standard Contractual Clauses approved by the Brazilian National Data Protection
                  Authority (ANPD) in accordance with LGPD requirements.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.3 Transfer Impact Assessments
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  We conduct Transfer Impact Assessments to evaluate the level of protection in
                  destination countries and implement supplementary measures where necessary. These
                  assessments consider local laws, government access requests, and available legal
                  remedies.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.4 Your Rights Regarding International Transfers
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  You may request a copy of the safeguards we have put in place for international
                  data transfers by contacting{' '}
                  <a
                    href="mailto:privacy@chat.co"
                    className="text-primary-600 hover:text-primary-700">
                    privacy@chat.co
                  </a>
                  .
                </p>
              </section>

              {/* Section 8: Data Retention */}
              <section id="data-retention" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">8. Data Retention</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  We retain your personal data only for as long as necessary to fulfill the purposes
                  for which it was collected, including to satisfy legal, accounting, or reporting
                  requirements.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">8.1 Retention Periods</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Data Type
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Retention Period
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Account Information
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Duration of account + 30 days after deletion
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Customer Content (Knowledge Bases)
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Duration of account + 30 days after deletion
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Chat Logs
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Per plan settings (or until deleted by user)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Payment Records
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          7 years (tax/accounting requirements)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Security Logs
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">1 year</td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Marketing Preferences
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Until withdrawn + 30 days
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  8.2 Criteria for Retention
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When determining retention periods, we consider:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>The amount, nature, and sensitivity of the data</li>
                  <li>The potential risk of harm from unauthorized use or disclosure</li>
                  <li>The purposes for which we process the data</li>
                  <li>Whether we can achieve those purposes through other means</li>
                  <li>Applicable legal, regulatory, tax, accounting, or other requirements</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">8.3 Data Deletion</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  When retention periods expire or you request deletion, we will securely delete or
                  anonymize your personal data, unless we are required to retain it for legal
                  compliance. Deletion from backup systems may take up to 90 days.
                </p>
                <h3
                  id="connector-retention"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  8.4 Connected Application Records
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Expert consultations create Chat.co conversation records for follow-up questions
                  and records of credit usage. Chat.co also stores connection records, operation
                  results, source references, and supporting excerpts or files needed to deliver
                  answers, handle retries without duplicate charges, and reopen evidence. These
                  records are subject to Chat.co’s account-retention and deletion processes;
                  conversation messages follow the applicable conversation settings.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  For connector copies linked to an uploaded document or a saved conversation,
                  cleanup removes copied evidence, generated files, and affected cached results
                  after the linked original is permanently removed. Archiving or recoverable
                  deletion does not itself trigger that permanent-copy cleanup. Other source
                  references remain subject to source availability and account retention. Billing
                  records follow their applicable retention requirements.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Disconnecting an application revokes its future access and does not itself delete
                  Chat.co conversations or billing records. For a deletion request, contact{' '}
                  <a href="mailto:privacy@chat.co" className="text-primary-600 underline">
                    privacy@chat.co
                  </a>
                  . Request deletion of copies held by an external application from that
                  application’s provider.
                </p>
              </section>

              {/* Section 9: Cookies and Tracking Technologies */}
              <section id="cookies" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  9. Cookies and Tracking Technologies
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  We use cookies, pixels, and similar technologies to collect information about your
                  browsing activities and to distinguish you from other users.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">9.1 What Are Cookies?</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Cookies are small text files placed on your device when you visit a website. They
                  are widely used to make websites work more efficiently and to provide information
                  to website owners.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.2 Types of Cookies We Use
                </h3>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.2.1 Strictly Necessary Cookies
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  These cookies are essential for the website to function and cannot be disabled.
                  They include:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Authentication cookies (to keep you logged in)</li>
                  <li>Security cookies (to prevent fraud and protect the site)</li>
                  <li>Session cookies (to remember your preferences during a session)</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.2.2 Performance and Analytics Cookies
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  These cookies help us understand how visitors interact with our website:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Google Analytics 4:</strong> With your consent, collects page views,
                    interactions, session activity, referral information, browser and device
                    details, approximate location, and pseudonymous cookie identifiers to help us
                    understand website usage and improve our Services. Google processes this data to
                    provide analytics reports. Cookie identifiers can distinguish browsers and
                    visits; this data is not necessarily anonymous. Without your consent, Google
                    receives only the cookieless signals described in Section 9.4.1.
                  </li>
                  <li>
                    <strong>Internal analytics:</strong> Tracks feature usage and performance
                    metrics
                  </li>
                </ul>

                <p className="mt-4 text-base leading-relaxed text-gray-700">
                  Google Analytics uses first-party cookies such as <code>_ga</code> and{' '}
                  <code>_ga_*</code> to distinguish visitors and maintain session information.
                  Google may process analytics data on servers outside your country. Learn more
                  about{' '}
                  <a
                    href="https://policies.google.com/technologies/partner-sites"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline">
                    how Google uses information from sites that use its services
                  </a>{' '}
                  and read{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline">
                    Google&apos;s Privacy Policy
                  </a>
                  . Google also provides a{' '}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline">
                    Google Analytics opt-out browser add-on
                  </a>
                  .
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.2.3 Functional Cookies
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  These cookies enable enhanced functionality and personalization:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Language and region preferences</li>
                  <li>Customized layouts and settings</li>
                  <li>Previously viewed content</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.2.4 Marketing Cookies
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  These cookies track your activity to deliver relevant advertising:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Google Ads cookies, including <code>_gcl_*</code>, help measure advertising
                    conversions and show relevant ads on other sites after you consent. Without
                    consent, conversions are measured only through the cookieless signals described
                    in Section 9.4.1
                  </li>
                  <li>Advertising cookies from third-party networks</li>
                  <li>Social media cookies for sharing features</li>
                  <li>Retargeting cookies for personalized ads</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.3 Third-Party Cookies
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Some cookies are placed by third-party services that appear on our pages,
                  including Google Analytics, Google Ads, Stripe (payment processing), and social
                  media platforms. Google&apos;s tags may also set first-party cookies on our
                  domain.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.4 Managing Your Cookie Preferences
                </h3>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.4.1 Cookie Consent Banner
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  On your first visit, our cookie consent banner lets you accept or decline optional
                  Google Analytics and Google Ads cookies. We use Google Consent Mode: Google&apos;s
                  tag loads on our pages, but until you accept it does not set or read Analytics or
                  advertising cookies, and it removes ad click identifiers from what it sends.
                  Without your consent, and after you decline or send a Global Privacy Control
                  signal, Google receives only cookieless signals — for example, that a page was
                  viewed or an account was created, together with technical details such as browser
                  type, timestamp, and page address — which Google may use to estimate aggregate
                  measurement. These signals do not include Analytics or advertising cookie
                  identifiers or your Chat.co account identifier. While consent is not granted, an
                  ad click identifier may remain in the page address as you move between our pages
                  instead of being stored in a cookie. Advertising audiences and personalized ads
                  are used only after you accept. You can change your choice at any time using
                  Cookie Settings in the website footer. Withdrawing consent stops future collection
                  through these tags and attempts to remove their cookies from your browser; it does
                  not automatically delete data already collected by Google. We remember your choice
                  for 180 days, after which we ask again. A Global Privacy Control signal is treated
                  as an automatic opt-out. Declining optional cookies does not prevent use of the
                  Services, and accepting our Terms does not replace your cookie choice.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.4.2 Browser Settings
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Most web browsers allow you to manage cookies through their settings. You can
                  typically:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>View cookies stored on your device</li>
                  <li>Delete all or specific cookies</li>
                  <li>Block all cookies or only third-party cookies</li>
                  <li>Set preferences for specific websites</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  9.4.3 Do Not Track
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Some browsers offer a "Do Not Track" (DNT) feature. We currently do not respond to
                  DNT signals, but we do honor the Global Privacy Control (GPC) signal as required
                  by California law.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.5 Consequences of Disabling Cookies
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If you disable or reject cookies, some features of our website may not function
                  properly. Strictly necessary cookies cannot be disabled as they are required for
                  the website to operate.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.6 Embedded Chat Widgets
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When our customers embed Chat.co widgets on their websites:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    The widget uses session cookies for functionality (conversation continuity)
                  </li>
                  <li>Analytics cookies are only loaded if consent is provided</li>
                  <li>
                    Our customers are responsible for obtaining cookie consent on their websites
                  </li>
                  <li>
                    We provide documentation to help customers implement consent-controlled widget
                    loading
                  </li>
                </ul>
                <h3
                  id="mobile-analytics"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  9.7 Mobile App Analytics
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Our iOS and Android apps use Google Analytics for Firebase only if you opt in. It
                  collects app usage and session events, in-app purchase and subscription activity
                  (such as product and price), app version and device information, approximate
                  location, and a pseudonymous app-instance identifier. Google processes this
                  information to help us understand app usage and improve the Services and may
                  process it outside your country. This data is not necessarily anonymous.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We do not send chat content, uploaded documents, email addresses, account IDs, bot
                  or conversation IDs, or error-message text in our mobile analytics events.
                  Advertising identifier collection and advertising personalization are disabled.
                </p>
                <p className="text-base leading-relaxed text-gray-700">
                  You can decline without losing access to app features, or change your choice at
                  any time using Usage analytics in the app&apos;s Account screen. Turning it off
                  stops future collection and resets locally stored analytics data and the
                  app-instance identifier. This does not delete data already received by Google; for
                  deletion requests, see Your Privacy Rights below. Your app choice is separate from
                  website Cookie Settings, and the Google Analytics browser opt-out add-on does not
                  control mobile app analytics.
                </p>
              </section>

              {/* Section 10: Your Privacy Rights */}
              <section id="your-rights" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">10. Your Privacy Rights</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  Depending on your location, you may have certain rights regarding your personal
                  data. This section describes those rights and how to exercise them.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.1 Rights Available to All Users
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Regardless of your location, you can:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Access your account settings and update your information</li>
                  <li>Download your data through the platform's export features</li>
                  <li>Delete your account and associated data</li>
                  <li>
                    Opt out of marketing communications by clicking "unsubscribe" in any email
                  </li>
                  <li>Manage your cookie preferences</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.2 Rights for California Residents (CCPA/CPRA)
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  If you are a California resident, you have the following rights under the
                  California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.1 Right to Know
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  You have the right to request that we disclose:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>The categories of personal information we have collected about you</li>
                  <li>The categories of sources from which we collected personal information</li>
                  <li>Our business or commercial purpose for collecting personal information</li>
                  <li>The categories of third parties with whom we share personal information</li>
                  <li>The specific pieces of personal information we have collected about you</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.2 Right to Delete
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  You have the right to request deletion of personal information we have collected,
                  subject to certain exceptions (legal obligations, security, completing
                  transactions, etc.).
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.3 Right to Correct
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  You have the right to request correction of inaccurate personal information.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.4 Right to Opt-Out of Sale/Sharing
                </h4>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We do not sell your personal information. However, some of our use of third-party
                  cookies for analytics and advertising may constitute "sharing" under CPRA. You can
                  opt out by:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Clicking "Do Not Sell or Share My Personal Information" on our website</li>
                  <li>Using the Global Privacy Control (GPC) browser setting</li>
                  <li>Adjusting cookie preferences in our consent banner</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.5 Right to Limit Use of Sensitive Personal Information
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  We do not collect sensitive personal information as defined by CPRA.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.6 Right to Non-Discrimination
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  We will not discriminate against you for exercising your privacy rights.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.7 Authorized Agents
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  You may designate an authorized agent to submit requests on your behalf. We may
                  require verification of your identity and the agent's authority.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.2.8 Response Timing
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  We will acknowledge your request within 10 business days and provide a substantive
                  response within 45 days (which may be extended by an additional 45 days with
                  notice).
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.3 Rights for EEA, UK, and Swiss Residents (GDPR)
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If you are located in the European Economic Area, United Kingdom, or Switzerland,
                  you have the following rights under the General Data Protection Regulation (GDPR)
                  or equivalent legislation:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Right of Access:</strong> Obtain confirmation of whether we process your
                    data and receive a copy
                  </li>
                  <li>
                    <strong>Right to Rectification:</strong> Have inaccurate personal data corrected
                  </li>
                  <li>
                    <strong>Right to Erasure:</strong> Have your personal data deleted in certain
                    circumstances
                  </li>
                  <li>
                    <strong>Right to Restriction:</strong> Restrict processing in certain
                    circumstances
                  </li>
                  <li>
                    <strong>Right to Data Portability:</strong> Receive your data in a structured,
                    machine-readable format
                  </li>
                  <li>
                    <strong>Right to Object:</strong> Object to processing based on legitimate
                    interests or for direct marketing
                  </li>
                  <li>
                    <strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where
                    we rely on consent
                  </li>
                  <li>
                    <strong>Right Not to Be Subject to Automated Decision-Making:</strong> Not be
                    subject to decisions based solely on automated processing that produce legal
                    effects
                  </li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.3.1 Right to Lodge a Complaint
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  You have the right to lodge a complaint with a supervisory authority in the EU
                  member state of your residence, place of work, or where the alleged infringement
                  occurred.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  10.3.2 Response Timing
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  We will respond to your request within 30 days, which may be extended by up to two
                  additional months for complex requests.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.4 Rights for Brazilian Residents (LGPD)
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If you are located in Brazil, you have the following rights under the Lei Geral de
                  Proteção de Dados (LGPD):
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Confirmation of Processing:</strong> Confirm whether we process your
                    data
                  </li>
                  <li>
                    <strong>Access:</strong> Access your personal data
                  </li>
                  <li>
                    <strong>Correction:</strong> Correct incomplete, inaccurate, or outdated data
                  </li>
                  <li>
                    <strong>Anonymization, Blocking, or Deletion:</strong> For unnecessary or
                    excessive data
                  </li>
                  <li>
                    <strong>Portability:</strong> Transfer data to another service provider
                  </li>
                  <li>
                    <strong>Deletion:</strong> Delete data processed with your consent
                  </li>
                  <li>
                    <strong>Information about Sharing:</strong> Know which entities received your
                    data
                  </li>
                  <li>
                    <strong>Information about Consent:</strong> Be informed about the consequences
                    of denying consent
                  </li>
                  <li>
                    <strong>Withdrawal of Consent:</strong> Revoke consent at any time
                  </li>
                  <li>
                    <strong>Right to Petition:</strong> Submit a complaint to the ANPD (National
                    Data Protection Authority)
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.5 How to Exercise Your Rights
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  To exercise any of your privacy rights, please contact us:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Email:</strong>{' '}
                    <a
                      href="mailto:privacy@chat.co"
                      className="text-primary-600 hover:text-primary-700">
                      privacy@chat.co
                    </a>
                  </li>
                  <li>
                    <strong>Mail:</strong> SA Cyber LLC, Attn: Privacy Team
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.6 Identity Verification
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  To protect your privacy, we may need to verify your identity before fulfilling
                  your request. We may ask you to provide information that matches our records or
                  verify your request through your account email address.
                </p>
              </section>

              {/* Section 11: Data Security */}
              <section id="data-security" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">11. Data Security</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  We implement appropriate technical and organizational measures to protect your
                  personal data against unauthorized access, alteration, disclosure, or destruction.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  11.1 Security Measures
                </h3>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  11.1.1 Infrastructure Security
                </h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    SA CYBER LLC&apos;s information security management system supporting Chat.co is
                    certified to ISO/IEC 27001:2022 (Certificate GCAI-ISMS-LM73), valid through
                    August 28, 2029.{' '}
                    <a
                      href="/certifications/sa-cyber-iso-27001-2022.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary-600 underline hover:text-primary-700">
                      View certificate
                    </a>
                    .
                  </li>
                  <li>AWS infrastructure maintains SOC 2 Type II and ISO 27001 certifications</li>
                  <li>Virtual Private Cloud (VPC) isolation</li>
                  <li>Distributed denial-of-service (DDoS) protection</li>
                  <li>Web Application Firewall (WAF)</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  11.1.2 Data Encryption
                </h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>TLS 1.3 encryption for data in transit</li>
                  <li>AES-256 encryption for data at rest</li>
                  <li>AWS Key Management Service (KMS) for encryption key management</li>
                  <li>Encrypted database connections</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  11.1.3 Access Controls
                </h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Role-based access control (RBAC)</li>
                  <li>Multi-factor authentication for administrative access</li>
                  <li>Principle of least privilege</li>
                  <li>Regular access reviews</li>
                  <li>Comprehensive audit logging</li>
                </ul>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  11.1.4 Monitoring and Response
                </h4>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>24/7 security monitoring</li>
                  <li>Automated threat detection</li>
                  <li>Incident response procedures</li>
                  <li>Regular vulnerability assessments</li>
                  <li>Annual third-party penetration testing</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  11.2 Employee Security
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Background checks for employees with data access</li>
                  <li>Security awareness training</li>
                  <li>Confidentiality agreements</li>
                  <li>Secure development practices</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  11.3 Your Responsibilities
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  While we take security seriously, you also play a role in protecting your data:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Use strong, unique passwords for your account</li>
                  <li>Enable two-factor authentication when available</li>
                  <li>Keep your login credentials confidential</li>
                  <li>Log out of shared devices</li>
                  <li>Report any suspected security incidents promptly</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  11.4 Data Breach Notification
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  In the event of a data breach that affects your personal information, we will:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Notify relevant supervisory authorities within 72 hours as required by GDPR
                  </li>
                  <li>Notify affected individuals without undue delay when there is high risk</li>
                  <li>Notify the Brazilian ANPD within 3 working days as required by LGPD</li>
                  <li>
                    Comply with applicable state breach notification laws in the United States
                  </li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  Our notification will describe the nature of the breach, the data involved, the
                  likely consequences, and the measures taken or proposed to address the breach.
                </p>
              </section>

              {/* Section 12: California-Specific Notices */}
              <section id="california" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  12. California-Specific Notices
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  This section provides additional disclosures required under California law.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.1 Categories of Personal Information Collected
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  In the preceding 12 months, we have collected the following categories of personal
                  information:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Identifiers (name, email, IP address)</li>
                  <li>Commercial information (transaction history, subscription details)</li>
                  <li>Internet activity (usage data, browsing history on our site)</li>
                  <li>Professional information (job title, company name)</li>
                  <li>Inferences drawn from the above</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.2 Sources of Personal Information
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Directly from you (account registration, support requests)</li>
                  <li>Automatically from your devices (cookies, usage data)</li>
                  <li>From third parties (single sign-on providers, analytics partners)</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.3 Business Purposes for Collection
                </h3>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Providing and maintaining our Services</li>
                  <li>Processing transactions</li>
                  <li>Customer support</li>
                  <li>Security and fraud prevention</li>
                  <li>Analytics and service improvement</li>
                  <li>Marketing (with consent)</li>
                  <li>Legal compliance</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.4 Disclosure of Personal Information
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We may disclose personal information to the following categories of third parties:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Service providers (hosting, payment processing, analytics)</li>
                  <li>Business partners (with your consent)</li>
                  <li>Legal authorities (when required by law)</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.5 Sale and Sharing of Personal Information
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  <strong>We do not sell your personal information.</strong>
                </p>
                <p className="text-base leading-relaxed text-gray-700">
                  We may "share" (as defined by CPRA) personal information with advertising partners
                  through cookies. You can opt out using the methods described in Section 10.2.4.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.6 Financial Incentives
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  We do not offer financial incentives for the collection, sale, or deletion of
                  personal information.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">12.7 Shine the Light</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  California Civil Code Section 1798.83 permits California residents to request
                  information about disclosure of personal information to third parties for direct
                  marketing purposes. We do not share personal information with third parties for
                  their direct marketing purposes.
                </p>
              </section>

              {/* Section 13: Changes to This Privacy Policy */}
              <section id="changes" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  13. Changes to This Privacy Policy
                </h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  We may update this Privacy Policy from time to time to reflect changes in our
                  practices, technologies, legal requirements, or other factors.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  13.1 Notification of Changes
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  When we make material changes to this Privacy Policy, we will:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Update the "Last Updated" date at the top of this policy</li>
                  <li>Post the revised policy on our website</li>
                  <li>Send email notification to registered users for significant changes</li>
                  <li>Provide prominent notice on our platform when you log in</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  13.2 Your Continued Use
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Your continued use of our Services after the effective date of any changes
                  constitutes your acceptance of the revised Privacy Policy. If you do not agree to
                  the revised policy, you should stop using our Services and contact us to delete
                  your account.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  13.3 Previous Versions
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Previous versions of this Privacy Policy are available upon request. Contact{' '}
                  <a
                    href="mailto:privacy@chat.co"
                    className="text-primary-600 hover:text-primary-700">
                    privacy@chat.co
                  </a>{' '}
                  to request archived versions.
                </p>
              </section>

              {/* Section 14: Contact Us */}
              <section id="contact" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">14. Contact Us</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or
                  our data practices, please contact us:
                </p>

                <div className="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-6">
                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      General Privacy Inquiries
                    </p>
                    <p className="text-base font-medium text-gray-900">
                      SA Cyber LLC d/b/a Chat.co
                    </p>
                    <p className="mt-1 text-base text-gray-700">
                      Email:{' '}
                      <a
                        href="mailto:privacy@chat.co"
                        className="text-primary-600 hover:text-primary-700">
                        privacy@chat.co
                      </a>
                    </p>
                    <p className="text-base text-gray-700">
                      Website:{' '}
                      <a
                        href="https://chat.co/contact-us#contact-us"
                        className="text-primary-600 hover:text-primary-700">
                        https://chat.co/contact-us
                      </a>
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Data Protection Officer
                    </p>
                    <p className="text-base text-gray-700">
                      Email:{' '}
                      <a
                        href="mailto:dpo@chat.co"
                        className="text-primary-600 hover:text-primary-700">
                        dpo@chat.co
                      </a>
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Privacy Rights Requests
                    </p>
                    <p className="text-base text-gray-700">
                      Email:{' '}
                      <a
                        href="mailto:privacy@chat.co"
                        className="text-primary-600 hover:text-primary-700">
                        privacy@chat.co
                      </a>
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Complaints
                    </p>
                    <p className="text-base leading-relaxed text-gray-700">
                      If you are not satisfied with our response to your privacy concerns, you have
                      the right to lodge a complaint with:
                    </p>
                    <ul className="ml-4 mt-2 list-disc space-y-1 text-base text-gray-700">
                      <li>Your local data protection authority (for EU/EEA residents)</li>
                      <li>The Information Commissioner's Office (for UK residents)</li>
                      <li>
                        The ANPD - Autoridade Nacional de Proteção de Dados (for Brazilian
                        residents)
                      </li>
                      <li>The California Attorney General (for California residents)</li>
                    </ul>
                    <p className="mt-3 text-base text-gray-700">
                      We encourage you to contact us first so we can try to resolve your concerns
                      directly.
                    </p>
                  </div>
                </div>

                <div className="mt-8 border-t border-gray-200 pt-8 text-center">
                  <p className="text-sm text-gray-500">
                    &copy; 2026 SA Cyber LLC d/b/a Chat.co. All rights reserved.
                  </p>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
